import React from 'react';
import SvgIcon from '@/components/Icon/SvgIcon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MerchantOrderStatusEnum } from '@/graphql/generatedTypes';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';
import {
  StatusPresets,
  EVENT_ORDER_ALLOWED_STATUSES,
  getPresetKeyForStatus,
  getStatusLabelKeys,
  StatusVariant,
} from './orderStatusConfig';
import { IconName } from '@/utils/iconName';

export type OrderStatusSelectProps = {
  value?: MerchantOrderStatusEnum | null;
  onChange: (status: MerchantOrderStatusEnum) => void;
  disabled?: boolean;
  className?: string;
  allowedStatuses?: MerchantOrderStatusEnum[];
  /** Statuses that are visible but cannot be selected */
  disabledStatuses?: MerchantOrderStatusEnum[];
  /** 'client' for customer-facing (default), 'admin' for admin view */
  variant?: StatusVariant;
};

function StatusOption({
  status,
  variant = 'client',
}: {
  status: MerchantOrderStatusEnum;
  variant?: StatusVariant;
}) {
  const presetKey = getPresetKeyForStatus(status, variant);
  const preset = StatusPresets[presetKey];
  const labelKeys = getStatusLabelKeys(variant);
  const labelKey = labelKeys[status] || preset.labelKey;

  return (
    <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
      <SvgIcon
        name={preset.iconName as IconName}
        className="tw:size-4 tw:shrink-0"
      />
      <span className="tw:truncate">{i18n.t(labelKey)}</span>
    </div>
  );
}

export default function OrderStatusSelect({
  value,
  onChange,
  disabled = false,
  className,
  allowedStatuses = EVENT_ORDER_ALLOWED_STATUSES,
  disabledStatuses = [],
  variant = 'client',
}: OrderStatusSelectProps) {
  const currentPresetKey = value ? getPresetKeyForStatus(value, variant) : null;
  const currentPreset = currentPresetKey
    ? StatusPresets[currentPresetKey]
    : null;

  return (
    <Select
      value={value || undefined}
      onValueChange={(newValue) =>
        onChange(newValue as MerchantOrderStatusEnum)
      }
      disabled={disabled}
    >
      <SelectTrigger
        className={cn(
          'tw:w-[190px] tw:[&>svg]:text-current',
          currentPreset?.className,
          'tw:border tw:border-input',
          className,
        )}
      >
        <SelectValue
          placeholder={i18n.t('Common:selectStatus') || 'Select status'}
        >
          {value ? (
            <StatusOption status={value} variant={variant} />
          ) : undefined}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {allowedStatuses.map((status) => {
          const isStatusDisabled = disabledStatuses.includes(status);
          return (
            <SelectItem key={status} value={status} disabled={isStatusDisabled}>
              <StatusOption status={status} variant={variant} />
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
